import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  useImportSniperMarkets,
  useCreateSniperMarkets,
} from '../../api/useSniper';
import type {
  ImportSniperMarketsDto,
  SniperCounter,
  SniperMarketDraft,
  SniperPeriod,
  SniperTeam,
} from '../../types/sniper.types';
import { SNIPER_COUNTERS } from '../../types/sniper.types';
import {
  candidateToDraft,
  counterLabel,
  DEFAULT_CONFIDENCE_GATE,
  DEFAULT_MAX_PRICE,
  DEFAULT_VAR_CONFIRMED_FLOOR_PRICE,
  DEFAULT_VAR_EXIT_PRICE,
  draftIncompleteReason,
  isDraftComplete,
  normalizeDecimalInput,
  SNIPER_FORCED_LINE,
  venueColor,
  venueLabel,
} from '../../utils/sniperArm';

/** Bulk defaults stamped onto every draft by "Apply to all". Blank = null. */
interface BulkDefaults {
  size: number | null;
  maxPrice: number | null;
  varExitPrice: number | null;
  varConfirmedFloorPrice: number | null;
  confidencePct: number | null;
}

/**
 * Controlled decimal input that PRESERVES in-progress text (so `"0."` survives
 * long enough to reach `"0.5"`) and accepts a comma decimal separator. The prior
 * `value={String(draft.x)}` + `Number(...)` round-trip dropped the trailing dot
 * (`Number("0.") === 0` → re-rendered to `"0"`), making decimals untypable.
 *
 * Holds a LOCAL `text` string and emits a parsed number (or `null` for an empty
 * / bare-dot field). A `useEffect` syncs from the incoming `value` ONLY when it
 * diverges from what `text` currently parses to — so an external update (the
 * bulk "Apply to all", a reset, the initial guess) lands without clobbering what
 * the operator is mid-typing.
 */
function DecimalField({
  value,
  onValueChange,
  label,
  width,
  ariaLabel,
  disabled,
  helperText,
}: {
  value: number | null;
  onValueChange: (n: number | null) => void;
  label: string;
  width: number;
  ariaLabel: string;
  disabled?: boolean;
  helperText?: string;
}) {
  const [text, setText] = useState<string>(value == null ? '' : String(value));

  useEffect(() => {
    const current = text === '' || text === '.' ? null : Number(text);
    if (value !== current) {
      setText(value == null ? '' : String(value));
    }
    // Intentionally keyed on the incoming numeric `value` only; reading `text`
    // here would re-sync on every keystroke and clobber in-progress typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <TextField
      size="small"
      label={label}
      value={text}
      disabled={disabled}
      helperText={helperText}
      onChange={(e) => {
        const normalized = normalizeDecimalInput(e.target.value);
        setText(normalized);
        onValueChange(
          normalized === '' || normalized === '.' ? null : Number(normalized),
        );
      }}
      inputProps={{ inputMode: 'decimal', 'aria-label': ariaLabel }}
      sx={{ width }}
    />
  );
}

/**
 * Compact labelled toggle for a boolean VAR-defense flag. The MUI `Switch` is a
 * real DOM element so the `Tooltip` anchors directly to the `FormControlLabel`
 * (no wrapping span needed, unlike the plain-function `DecimalField`).
 */
function FlagSwitch({
  checked,
  onCheckedChange,
  label,
  tooltip,
  ariaLabel,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  label: string;
  tooltip: string;
  ariaLabel: string;
}) {
  return (
    <Tooltip title={tooltip}>
      <FormControlLabel
        sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 11, color: 'common.white' } }}
        control={
          <Switch
            size="small"
            checked={checked}
            onChange={(e) => onCheckedChange(e.target.checked)}
            inputProps={{ 'aria-label': ariaLabel }}
          />
        }
        label={label}
      />
    </Tooltip>
  );
}

/** One editable candidate row inside the review step. Exported for testing. */
export function CandidateRow({
  draft,
  homeTeam,
  awayTeam,
  onChange,
}: {
  draft: SniperMarketDraft;
  homeTeam: string;
  awayTeam: string;
  onChange: (next: SniperMarketDraft) => void;
}) {
  const teamRequired =
    draft.counter === 'team_goals' ||
    draft.counter === 'team_corners' ||
    draft.counter === 'first_team_to_score';
  const isCorners =
    draft.counter === 'match_corners' || draft.counter === 'team_corners';
  // BTTS/FTTS have a FORCED line the operator does NOT own.
  const lineFixed =
    draft.counter === 'both_teams_to_score' ||
    draft.counter === 'first_team_to_score';
  const reason = draftIncompleteReason(draft);
  const complete = reason === null;
  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{
        py: 0.5,
        px: 0.75,
        borderRadius: 1,
        border: 1,
        borderColor: complete ? 'divider' : 'warning.main',
        flexWrap: 'nowrap',
        overflowX: 'auto',
      }}
    >
      <Chip
        size="small"
        label={venueLabel(draft.venue)}
        sx={{ bgcolor: venueColor(draft.venue), color: '#fff', fontWeight: 700 }}
      />
      <Typography variant="body2" sx={{ minWidth: 120, flex: 1, fontSize: 12 }}>
        {draft.marketLabel}
      </Typography>

      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel id={`counter-${draft.venue}-${draft.marketId}`}>
          Counter
        </InputLabel>
        <Select
          labelId={`counter-${draft.venue}-${draft.marketId}`}
          label="Counter"
          displayEmpty
          value={draft.counter ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            const counter = (raw === '' ? null : raw) as SniperCounter | null;
            const nowTeam =
              counter === 'team_goals' ||
              counter === 'team_corners' ||
              counter === 'first_team_to_score';
            const nowCorners =
              counter === 'match_corners' || counter === 'team_corners';
            // BTTS/FTTS: force the contract line (1.5/0.5); otherwise keep the
            // operator's line. BTTS is match-wide (team null), FTTS team-scoped.
            const forcedLine = counter ? SNIPER_FORCED_LINE[counter] : undefined;
            onChange({
              ...draft,
              counter,
              // Set a team iff team_* / FTTS; clear it for match-wide / unset.
              team: nowTeam ? (draft.team ?? 'home') : null,
              // Corners are full-match only — force the half back to 'full' so
              // the row can't be left in a server-rejected state. Goal-family
              // (incl. BTTS/FTTS) KEEP the operator's chosen period.
              period: nowCorners ? 'full' : draft.period,
              line: forcedLine != null ? forcedLine : draft.line,
            });
          }}
        >
          <MenuItem value="">
            <em>— select —</em>
          </MenuItem>
          {SNIPER_COUNTERS.map((c) => (
            <MenuItem key={c} value={c}>
              {counterLabel(c)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 95 }} disabled={!teamRequired}>
        <InputLabel id={`team-${draft.venue}-${draft.marketId}`}>Team</InputLabel>
        <Select
          labelId={`team-${draft.venue}-${draft.marketId}`}
          label="Team"
          value={teamRequired ? (draft.team ?? '') : ''}
          onChange={(e) =>
            onChange({ ...draft, team: (e.target.value || null) as SniperTeam | null })
          }
        >
          <MenuItem value="home">{homeTeam || 'Home'}</MenuItem>
          <MenuItem value="away">{awayTeam || 'Away'}</MenuItem>
        </Select>
        {!teamRequired && (
          <FormHelperText>counter is match-wide</FormHelperText>
        )}
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 105 }} disabled={isCorners}>
        <InputLabel id={`period-${draft.venue}-${draft.marketId}`}>
          Period
        </InputLabel>
        <Select
          labelId={`period-${draft.venue}-${draft.marketId}`}
          label="Period"
          value={draft.period}
          onChange={(e) =>
            onChange({ ...draft, period: e.target.value as SniperPeriod })
          }
        >
          <MenuItem value="full">Full</MenuItem>
          <MenuItem value="first_half">1st Half</MenuItem>
          <MenuItem value="second_half">2nd Half</MenuItem>
        </Select>
        {isCorners && <FormHelperText>corners are full-match</FormHelperText>}
        {!isCorners &&
          draft.counter === 'both_teams_to_score' &&
          draft.period === 'second_half' && (
            <FormHelperText>2H BTTS is Polymarket-only</FormHelperText>
          )}
      </FormControl>

      <DecimalField
        label="Line"
        ariaLabel="line"
        width={62}
        value={draft.line}
        onValueChange={(n) => onChange({ ...draft, line: n })}
        disabled={lineFixed}
        helperText={
          lineFixed
            ? `fixed ${SNIPER_FORCED_LINE[draft.counter as SniperCounter]}`
            : undefined
        }
      />
      <DecimalField
        label="Size"
        ariaLabel="size"
        width={62}
        value={draft.size}
        onValueChange={(n) => onChange({ ...draft, size: n ?? 0 })}
      />
      <DecimalField
        label="Max $"
        ariaLabel="max price"
        width={62}
        value={draft.maxPrice}
        onValueChange={(n) => onChange({ ...draft, maxPrice: n ?? 0 })}
      />
      <Tooltip title="VAR exit — precautionary sell posted the moment a VAR review starts (Phase 1), before the cancellation is confirmed">
        <span>
          <DecimalField
            label="Exit $"
            ariaLabel="var exit price"
            width={62}
            value={draft.varExitPrice}
            onValueChange={(n) => onChange({ ...draft, varExitPrice: n ?? 0 })}
          />
        </span>
      </Tooltip>
      <Tooltip title="VAR floor — sell price once the goal cancellation is CONFIRMED (Phase 2, or any other revert)">
        <span>
          <DecimalField
            label="Floor $"
            ariaLabel="var confirmed floor price"
            width={62}
            value={draft.varConfirmedFloorPrice}
            onValueChange={(n) =>
              onChange({ ...draft, varConfirmedFloorPrice: n ?? 0 })
            }
          />
        </span>
      </Tooltip>

      {/* Auto-NO period-close tail — off by default. The No$ ceiling + Poly No
          tokenId only matter when enabled; Kalshi NO is native so its tokenId
          field is hidden (no id needed). */}
      <Tooltip title="Auto-NO — sweep the No/Under side at period close">
        <FormControlLabel
          sx={{ ml: 0 }}
          control={
            <Checkbox
              size="small"
              checked={draft.autoNoEnabled}
              onChange={(e) =>
                onChange({ ...draft, autoNoEnabled: e.target.checked })
              }
              inputProps={{ 'aria-label': 'auto-NO enabled' }}
            />
          }
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
              auto-NO
            </Typography>
          }
        />
      </Tooltip>
      <DecimalField
        label="No $"
        ariaLabel="no max price"
        width={62}
        value={draft.noMaxPrice}
        onValueChange={(n) => onChange({ ...draft, noMaxPrice: n })}
        disabled={!draft.autoNoEnabled}
      />
      <Tooltip title="Include extra time in the auto-NO tail (only meaningful for full / 2nd-half markets)">
        <FormControlLabel
          sx={{ ml: 0 }}
          control={
            <Switch
              size="small"
              checked={draft.autoNoIncludeExtraTime}
              disabled={!draft.autoNoEnabled || draft.period === 'first_half'}
              onChange={(e) =>
                onChange({
                  ...draft,
                  autoNoIncludeExtraTime: e.target.checked,
                })
              }
              inputProps={{ 'aria-label': 'auto-NO include extra time' }}
            />
          }
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
              ET
            </Typography>
          }
        />
      </Tooltip>

      <FlagSwitch
        label="Defense"
        ariaLabel="var defense enabled"
        tooltip="Master switch for the two-phase VAR defense (Phase 1 precautionary sell at the configured exit price + Phase 2 aggressive sell to the floor). Off = no VAR reaction."
        checked={draft.varDefenseEnabled}
        onCheckedChange={(v) => onChange({ ...draft, varDefenseEnabled: v })}
      />
      <FlagSwitch
        label="Prev"
        ariaLabel="var preventive exit enabled"
        tooltip="Phase 1 — post a precautionary sell at the configured exit price the moment a VAR follows our goal, before the annulment is confirmed."
        checked={draft.varPreventiveExitEnabled}
        onCheckedChange={(v) =>
          onChange({ ...draft, varPreventiveExitEnabled: v })
        }
      />
      <FlagSwitch
        label="Rebuy"
        ariaLabel="var rebuy enabled"
        tooltip="All-clear rebuy — restore the position if the VAR ultimately CONFIRMS the goal (the annulment was cleared)."
        checked={draft.varRebuyEnabled}
        onCheckedChange={(v) => onChange({ ...draft, varRebuyEnabled: v })}
      />
      {!complete && (
        <Tooltip title={`incomplete — ${reason}`}>
          <Chip
            size="small"
            color="warning"
            label={`incomplete · ${reason}`}
            sx={{ height: 20 }}
          />
        </Tooltip>
      )}
    </Stack>
  );
}

/**
 * Import-by-link flow: paste a Kalshi and/or Polymarket EVENT url → scrape
 * candidate markets (backend guesses counter/line) → review + fix each draft →
 * confirm-create. NEVER auto-persists a guess: only COMPLETE drafts
 * (`isDraftComplete`) are posted; `enabled` defaults OFF.
 */
export function SniperImportDialog({
  open,
  onClose,
  fixtureId,
  homeTeam,
  awayTeam,
}: {
  open: boolean;
  onClose: () => void;
  fixtureId: number;
  homeTeam: string;
  awayTeam: string;
}) {
  const [kalshiUrl, setKalshiUrl] = useState('');
  const [polyUrl, setPolyUrl] = useState('');
  const [drafts, setDrafts] = useState<SniperMarketDraft[] | null>(null);
  const [bulk, setBulk] = useState<BulkDefaults>({
    size: null,
    maxPrice: DEFAULT_MAX_PRICE,
    varExitPrice: DEFAULT_VAR_EXIT_PRICE,
    varConfirmedFloorPrice: DEFAULT_VAR_CONFIRMED_FLOOR_PRICE,
    confidencePct: Math.round(DEFAULT_CONFIDENCE_GATE * 100),
  });

  const importMut = useImportSniperMarkets();
  const createMut = useCreateSniperMarkets();

  /**
   * Stamp the bulk Size / Max $ / Exit $ / Confidence % onto EVERY draft (a
   * blank field leaves that column untouched). Each row's counter/line/team stay
   * as-is, so the valid match-goals totals become complete while unclassified
   * rows still need an explicit counter.
   */
  const applyToAll = () => {
    const {
      size: sizeNum,
      maxPrice: maxNum,
      varExitPrice: varNum,
      varConfirmedFloorPrice: floorNum,
      confidencePct: pctNum,
    } = bulk;
    setDrafts((prev) =>
      prev
        ? prev.map((d) => ({
            ...d,
            size: sizeNum != null ? sizeNum : d.size,
            maxPrice: maxNum != null ? maxNum : d.maxPrice,
            varExitPrice: varNum != null ? varNum : d.varExitPrice,
            varConfirmedFloorPrice:
              floorNum != null ? floorNum : d.varConfirmedFloorPrice,
            confidenceGate:
              pctNum != null
                ? Math.max(0, Math.min(1, pctNum / 100))
                : d.confidenceGate,
          }))
        : prev,
    );
  };

  const runImport = () => {
    const dto: ImportSniperMarketsDto = {};
    if (kalshiUrl.trim()) dto.kalshiEventUrl = kalshiUrl.trim();
    if (polyUrl.trim()) dto.polymarketEventUrl = polyUrl.trim();
    if (!dto.kalshiEventUrl && !dto.polymarketEventUrl) {
      toast.error('Paste at least one event URL.');
      return;
    }
    // Team names let the guesser auto-fill the team on team-goal markets.
    dto.homeTeam = homeTeam;
    dto.awayTeam = awayTeam;
    importMut.mutate(
      { fixtureId, dto },
      {
        onSuccess: (candidates) => {
          setDrafts(candidates.map(candidateToDraft));
          if (candidates.length === 0) {
            toast.info('No candidate markets found for those URLs.');
          }
        },
        onError: () => {
          toast.error('Import failed — check the URLs.');
        },
      },
    );
  };

  const completeDrafts = useMemo(
    () => (drafts ?? []).filter(isDraftComplete),
    [drafts],
  );

  const confirm = () => {
    if (completeDrafts.length === 0) return;
    createMut.mutate(
      { fixtureId, markets: completeDrafts },
      {
        onSuccess: () => {
          toast.success(`Added ${completeDrafts.length} market(s).`);
          handleClose();
        },
        onError: () => {
          toast.error('Failed to save markets.');
        },
      },
    );
  };

  const handleClose = () => {
    setDrafts(null);
    setKalshiUrl('');
    setPolyUrl('');
    setBulk({
      size: null,
      maxPrice: DEFAULT_MAX_PRICE,
      varExitPrice: DEFAULT_VAR_EXIT_PRICE,
      varConfirmedFloorPrice: DEFAULT_VAR_CONFIRMED_FLOOR_PRICE,
      confidencePct: Math.round(DEFAULT_CONFIDENCE_GATE * 100),
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth sx={{ '& .MuiDialog-paper': { maxWidth: '1600px', width: '96vw' } }}>
      <DialogTitle>Import markets by event link</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField
              size="small"
              label="Kalshi event URL"
              value={kalshiUrl}
              onChange={(e) => setKalshiUrl(e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="Polymarket event URL"
              value={polyUrl}
              onChange={(e) => setPolyUrl(e.target.value)}
              fullWidth
            />
            <Button
              variant="outlined"
              onClick={runImport}
              disabled={importMut.isLoading}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {importMut.isLoading ? 'Scraping…' : 'Scrape'}
            </Button>
          </Stack>

          {importMut.isLoading && (
            <Stack alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={22} />
            </Stack>
          )}

          {drafts != null && (
            <>
              <Divider />
              <Alert severity="info" sx={{ py: 0 }}>
                Review the auto-guessed counter / line. Only complete rows are
                saved; markets are created DISABLED (fail-safe OFF).
              </Alert>
              {drafts.length > 0 && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5 }}>
                    Bulk defaults:
                  </Typography>
                  <DecimalField
                    label="Size"
                    ariaLabel="bulk size"
                    width={90}
                    value={bulk.size}
                    onValueChange={(n) => setBulk((b) => ({ ...b, size: n }))}
                  />
                  <DecimalField
                    label="Max $"
                    ariaLabel="bulk max price"
                    width={90}
                    value={bulk.maxPrice}
                    onValueChange={(n) => setBulk((b) => ({ ...b, maxPrice: n }))}
                  />
                  <Tooltip title="VAR exit — precautionary sell posted the moment a VAR review starts (Phase 1), before the cancellation is confirmed">
                    <span>
                      <DecimalField
                        label="Exit $"
                        ariaLabel="bulk var exit price"
                        width={90}
                        value={bulk.varExitPrice}
                        onValueChange={(n) =>
                          setBulk((b) => ({ ...b, varExitPrice: n }))
                        }
                      />
                    </span>
                  </Tooltip>
                  <Tooltip title="VAR floor — sell price once the goal cancellation is CONFIRMED (Phase 2, or any other revert)">
                    <span>
                      <DecimalField
                        label="Floor $"
                        ariaLabel="bulk var confirmed floor price"
                        width={90}
                        value={bulk.varConfirmedFloorPrice}
                        onValueChange={(n) =>
                          setBulk((b) => ({ ...b, varConfirmedFloorPrice: n }))
                        }
                      />
                    </span>
                  </Tooltip>
                  <DecimalField
                    label="Confidence %"
                    ariaLabel="bulk confidence percent"
                    width={120}
                    value={bulk.confidencePct}
                    onValueChange={(n) =>
                      setBulk((b) => ({ ...b, confidencePct: n }))
                    }
                  />
                  <Button size="small" variant="outlined" onClick={applyToAll}>
                    Apply to all
                  </Button>
                </Stack>
              )}
              {drafts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No candidates.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {drafts.map((d, i) => (
                    <CandidateRow
                      key={`${d.venue}:${d.marketId}`}
                      draft={d}
                      homeTeam={homeTeam}
                      awayTeam={awayTeam}
                      onChange={(next) =>
                        setDrafts((prev) =>
                          prev
                            ? prev.map((p, j) => (j === i ? next : p))
                            : prev,
                        )
                      }
                    />
                  ))}
                </Stack>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="caption" color="text.secondary">
                  {completeDrafts.length} of {drafts.length} ready to save
                </Typography>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={confirm}
          disabled={completeDrafts.length === 0 || createMut.isLoading}
        >
          {createMut.isLoading
            ? 'Saving…'
            : `Add ${completeDrafts.length} market(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
